import React, { useEffect, useState } from "react";
import { Calendar, ExternalLink, ShieldCheck, ArrowRight } from "lucide-react";
import {
  getConsent,
  grantConsent,
  openCookieSettings,
  CONSENT_CHANGE_EVENT,
} from "./CookieConsent";

const BOOKING_URL =
  "https://outlook.office.com/book/DeineMikronhrstoffberatung@AlexanderRainerBuchner.onmicrosoft.com/?ismsaljsauthenabled";

function scrollTo(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function BookingSection() {
  const [marketingOk, setMarketingOk] = useState(false);

  useEffect(() => {
    const update = () => {
      const c = getConsent();
      setMarketingOk(!!c?.marketing);
    };
    update();
    const onChange = () => update();
    window.addEventListener(CONSENT_CHANGE_EVENT, onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener(CONSENT_CHANGE_EVENT, onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  const enable = () => {
    grantConsent("marketing");
    setMarketingOk(true);
  };

  return (
    <section
      id="booking-section"
      data-testid="booking-section"
      className="bg-[#F7F9FB] text-[#1F2937] py-24 lg:py-32"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <p className="text-xs sm:text-sm font-semibold tracking-[0.18em] uppercase text-[#2E89C4] mb-5">
              Termin buchen
            </p>
            <h2
              data-testid="booking-title"
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1]"
            >
              Such dir direkt deinen{" "}
              <span className="text-[#6FB04F]">freien Termin.</span>
            </h2>
            <p className="mt-6 text-[#1F2937]/70 text-base lg:text-lg leading-relaxed">
              Wähle einen Slot, der dir passt – das kostenlose Erstgespräch
              dauert ca. 20–30 Minuten und findet telefonisch oder per Video
              statt. Keine Verpflichtung, kein Verkaufsdruck.
            </p>

            <ul className="mt-8 space-y-3.5 text-sm">
              <li className="flex items-start gap-3">
                <span className="mt-1 h-5 w-5 shrink-0 rounded-full bg-[#6FB04F]/15 flex items-center justify-center">
                  <ShieldCheck size={12} className="text-[#6FB04F]" />
                </span>
                <span className="text-[#1F2937]/80">
                  Kostenlos & unverbindlich
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1 h-5 w-5 shrink-0 rounded-full bg-[#6FB04F]/15 flex items-center justify-center">
                  <ShieldCheck size={12} className="text-[#6FB04F]" />
                </span>
                <span className="text-[#1F2937]/80">
                  Telefon oder Video – du wählst
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1 h-5 w-5 shrink-0 rounded-full bg-[#6FB04F]/15 flex items-center justify-center">
                  <ShieldCheck size={12} className="text-[#6FB04F]" />
                </span>
                <span className="text-[#1F2937]/80">
                  Persönliches Gespräch – kein Bot, kein Formular-Algorithmus
                </span>
              </li>
            </ul>

            <p className="mt-10 text-sm text-[#1F2937]/55">
              Lieber schreiben?{" "}
              <button
                type="button"
                data-testid="booking-to-form-link"
                onClick={() => scrollTo("contact-form-section")}
                className="text-[#2E89C4] hover:underline font-medium"
              >
                Schick mir eine Nachricht
              </button>
              .
            </p>
          </div>

          <div className="lg:col-span-7 w-full">
            {marketingOk ? (
              <div
                data-testid="booking-iframe-wrapper"
                className="rounded-2xl border border-[#1F2937]/10 bg-white overflow-hidden shadow-[0_30px_80px_-40px_rgba(43,43,43,0.35)]"
              >
                <iframe
                  data-testid="booking-iframe"
                  src={BOOKING_URL}
                  title="Termin bei Alex Buchner Health buchen"
                  className="w-full h-[720px] sm:h-[800px] lg:h-[860px] border-0 bg-white"
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                />
                <div className="px-5 py-3 bg-[#F7F9FB] border-t border-[#1F2937]/10 flex items-center justify-between gap-3 text-xs text-[#1F2937]/55">
                  <span>Buchung läuft sicher über Microsoft Bookings.</span>
                  <a
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="booking-open-newtab"
                    className="inline-flex items-center gap-1.5 text-[#2E89C4] hover:underline font-medium"
                  >
                    Im neuen Tab öffnen
                    <ExternalLink size={12} />
                  </a>
                </div>
              </div>
            ) : (
              <div
                data-testid="booking-consent-gate"
                className="rounded-2xl border border-dashed border-[#1F2937]/20 bg-white/60 p-7 lg:p-10"
              >
                <div className="h-12 w-12 rounded-xl bg-[#2E89C4]/10 flex items-center justify-center mb-6">
                  <Calendar size={22} className="text-[#2E89C4]" />
                </div>
                <h3 className="font-display text-xl lg:text-2xl font-bold mb-3">
                  Buchungs-Widget eingebettet von Microsoft Bookings
                </h3>
                <p className="text-sm lg:text-[15px] text-[#1F2937]/70 leading-relaxed">
                  Um direkt hier einen Termin zu buchen, wird ein Drittinhalt von
                  Microsoft (outlook.office.com) geladen. Dabei werden technisch
                  notwendige Daten (z. B. IP-Adresse) an Microsoft übertragen.
                  Diese Einbettung ist Teil der Kategorie{" "}
                  <strong>„Marketing &amp; Drittinhalte"</strong> – du kannst sie
                  unten aktivieren oder direkt bei Microsoft buchen.
                </p>

                <div className="mt-7 flex flex-col sm:flex-row gap-3">
                  <button
                    data-testid="booking-enable-third-party"
                    onClick={enable}
                    className="btn-primary inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold"
                  >
                    Drittinhalte aktivieren &amp; buchen
                    <ArrowRight size={16} />
                  </button>
                  <a
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="booking-open-direct"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-medium border border-[#1F2937]/20 text-[#1F2937] hover:bg-[#1F2937]/5 transition"
                  >
                    Direkt bei Microsoft buchen
                    <ExternalLink size={14} />
                  </a>
                </div>

                <p className="mt-5 text-xs text-[#1F2937]/50">
                  Deine Wahl wird gespeichert. Du kannst sie jederzeit über die{" "}
                  <button
                    type="button"
                    onClick={openCookieSettings}
                    className="underline hover:text-[#2E89C4]"
                  >
                    Cookie-Einstellungen
                  </button>{" "}
                  ändern.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
